import type { RadioStation } from '@/mocks/radios';
import type { RadioLive } from '@/mocks/radioContent';
import { useAudioPlayer } from '@/context/AudioContext';

interface RadioLiveSectionProps {
  station: RadioStation;
  lives: RadioLive[];
}

export default function RadioLiveSection({ station, lives }: RadioLiveSectionProps) {
  const { play } = useAudioPlayer();

  const liveNow = lives.find((l) => l.status === 'en_vivo');
  const nextLive = lives.find((l) => l.status === 'programado');

  const handlePlay = () => {
    play(station);
  };

  if (!liveNow && !nextLive) return null;

  return (
    <section className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
      {/* Section Header */}
      <div className="flex items-center gap-2 px-5 py-3 border-b border-background-200/70">
        <div className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse"></div>
        <h3 className="text-base font-bold text-foreground-950 uppercase tracking-wide">Radio en Vivo</h3>
      </div>

      {/* Current Live */}
      {liveNow && (
        <div className="p-5">
          <div className="flex flex-col lg:flex-row gap-5">
            {/* Live Image / Player */}
            <div className="relative w-full lg:w-[420px] aspect-video rounded-lg overflow-hidden bg-background-200/70 flex-shrink-0">
              <img
                src={liveNow.imageUrl}
                alt={liveNow.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
              {/* Play Button */}
              <button
                onClick={handlePlay}
                className="absolute inset-0 flex items-center justify-center cursor-pointer group"
              >
                <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <i className="ri-play-fill text-3xl text-foreground-950 ml-0.5"></i>
                </div>
              </button>
              {/* Live Badge */}
              <div className="absolute top-3 left-3 flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-500 text-white text-xs font-bold">
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
                EN VIVO
              </div>
              {/* Viewers */}
              <div className="absolute bottom-3 right-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/60 text-white text-xs">
                <i className="ri-eye-line"></i>
                <span>{liveNow.viewers.toLocaleString()}</span>
              </div>
            </div>

            {/* Info Side */}
            <div className="flex-1 min-w-0 flex flex-col">
              <div className="flex items-center gap-2 mb-2">
                <div className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-semibold whitespace-nowrap">
                  {liveNow.duration}
                </div>
              </div>
              <h4 className="text-lg font-bold text-foreground-950 mb-1">{liveNow.title}</h4>
              <p className="text-sm text-foreground-500 mb-3">{liveNow.description}</p>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-full bg-background-200/70 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Professional%20radio%20host%20portrait%20with%20warm%20friendly%20smile%2C%20studio%20background%20softly%20blurred%2C%20warm%20lighting%2C%20editorial%20headshot%20photography&width=80&height=80&seq=host-live-generic&orientation=squarish"
                    alt={liveNow.hostName}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground-950">{liveNow.hostName}</p>
                  <p className="text-xs text-foreground-500">Locutor actual</p>
                </div>
              </div>

              {/* Reactions */}
              {liveNow.reactions.length > 0 && (
                <div className="flex items-center gap-3 flex-wrap mb-3">
                  {liveNow.reactions.map((r, i) => (
                    <button key={i} className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-background-100 border border-background-200/70 hover:bg-background-200/70 cursor-pointer transition-colors text-xs">
                      <span>{r.type}</span>
                      <span className="text-foreground-600 font-medium">{r.count}</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Info row */}
              <div className="flex flex-wrap items-center gap-x-3 md:gap-x-5 gap-y-2 text-sm mt-auto">
                <div className="flex items-center gap-2">
                  <i className="ri-user-line text-foreground-500"></i>
                  <span className="text-foreground-600">Locutor: <strong>{liveNow.hostName}</strong></span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-eye-line text-foreground-500"></i>
                  <span className="text-foreground-600">Espectadores: <strong>{liveNow.viewers.toLocaleString()}</strong></span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-time-line text-foreground-500"></i>
                  <span className="text-foreground-600">Duración: <strong>{liveNow.duration}</strong></span>
                </div>
              </div>

              {/* Recent comments preview */}
              {liveNow.comments.length > 0 && (
                <div className="mt-4 pt-4 border-t border-background-200/70">
                  <div className="flex items-center gap-2 mb-2.5">
                    <i className="ri-chat-3-line text-foreground-500 text-sm"></i>
                    <span className="text-xs font-semibold text-foreground-700">Últimos comentarios</span>
                  </div>
                  <div className="space-y-2.5 max-h-[140px] overflow-y-auto pr-1">
                    {liveNow.comments.slice(0, 5).map((comment) => (
                      <div key={comment.id} className="flex items-start gap-2.5">
                        <div className="w-6 h-6 rounded-full bg-background-200/70 overflow-hidden flex-shrink-0">
                          <img
                            src={comment.avatarUrl}
                            alt={comment.userName}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold text-foreground-950">{comment.userName}</span>
                            <span className="text-[10px] text-foreground-400">{comment.time}</span>
                          </div>
                          <p className="text-xs text-foreground-700">{comment.text}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Next Program */}
      {nextLive && (
        <div className="border-t border-background-200/70 px-5 py-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-background-200/70 overflow-hidden flex-shrink-0">
              <img
                src={nextLive.imageUrl}
                alt={nextLive.title}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="min-w-0">
              <p className="text-xs text-foreground-500 uppercase tracking-wide font-semibold">Próximo programa</p>
              <p className="text-sm font-semibold text-foreground-950 truncate">{nextLive.title}</p>
              <p className="text-xs text-foreground-500">
                {nextLive.scheduledDate} · {nextLive.scheduledTime} · {nextLive.hostName}
              </p>
            </div>
            <button className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-background-100 border border-background-200/70 text-xs font-semibold text-foreground-600 hover:bg-background-200/70 cursor-pointer transition-colors whitespace-nowrap ml-auto">
              <i className="ri-notification-3-line"></i>
              Recordar
            </button>
          </div>
        </div>
      )}

      {/* If no live but station exists — show play button */}
      {!liveNow && (
        <div className="p-5 flex items-center justify-center">
          <button
            onClick={handlePlay}
            className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm cursor-pointer transition-all whitespace-nowrap ${
              station.isLive
                ? 'bg-primary-500 text-background-50 dark:text-foreground-950 hover:bg-primary-600'
                : 'bg-amber-500 text-white hover:bg-amber-600'
            }`}
          >
            <i className={`${station.isLive ? 'ri-play-circle-fill' : 'ri-shuffle-line'} text-lg`}></i>
            {station.isLive ? `Escuchar ${station.frequency}` : 'Escuchar RFW Radio'}
          </button>
        </div>
      )}
    </section>
  );
}